import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { Button } from "./ui/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/ui/sheet";
import { Badge } from "./ui/ui/badge";
import { 
  Terminal, 
  Menu, 
  Home, 
  Calendar, 
  Zap, 
  Upload, 
  BookOpen, 
  LogOut,
  User,
  Mail
} from "lucide-react";
import logo from "../assets/hackai_logo_nobg_trim.png";
import { cn } from "../lib/utils";

interface NavbarProps {
  user: any;
}

const Navbar: React.FC<NavbarProps> = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const handleScrollToFAQ = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const faqSection = document.getElementById("faq");
        if (faqSection) {
          faqSection.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      const faqSection = document.getElementById("faq");
      if (faqSection) {
        faqSection.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsOpen(false);
  };

  const handleContactClick = () => {
    window.open("mailto:19bmiles@gmail.com", "_blank");
    setIsOpen(false);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = [
    { path: "/", label: "./home", icon: Home, action: null },
    { path: "/schedule", label: "./schedule", icon: Calendar, action: null },
    { path: "/events", label: "./events", icon: Zap, action: null },
    { path: "/resources", label: "./resources", icon: BookOpen, action: null },
    { 
      path: "#", 
      label: "./faq", 
      icon: Terminal, 
      action: handleScrollToFAQ 
    },
  ];

  const NavLink = ({ 
    path, 
    label, 
    icon: Icon, 
    onClick, 
    action 
  }: { 
    path: string; 
    label: string; 
    icon: any; 
    onClick?: () => void; 
    action?: (() => void) | null;
  }) => {
    const active = isActive(path) && !action;
    
    if (action) {
      return (
        <button
          onClick={() => {
            action();
            onClick?.();
          }}
          className={cn(
            "flex items-center space-x-2 px-3 py-2 rounded-md font-mono text-sm transition-all duration-300 group w-full text-left",
            "text-muted-foreground hover:text-primary hover:bg-primary/10 border border-transparent"
          )}
        >
          <Icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          <span>{label}</span>
        </button>
      );
    }

    return (
      <Link
        to={path}
        onClick={onClick}
        className={cn(
          "flex items-center space-x-2 px-3 py-2 rounded-md font-mono text-sm transition-all duration-300 group",
          active
            ? "bg-primary/20 text-primary border border-primary/30"
            : "text-muted-foreground hover:text-primary hover:bg-primary/10 border border-transparent"
        )}
      >
        <Icon className={cn(
          "h-4 w-4 transition-colors",
          active ? "text-primary" : "text-muted-foreground group-hover:text-primary"
        )} />
        <span>{label}</span>
        {active && (
          <Badge variant="outline" className="ml-auto border-primary/50 text-primary text-xs">
            active
          </Badge>
        )}
      </Link>
    );
  };

  return (
    <>
      {/* Background blur overlay */}
      <div className="fixed top-0 left-0 right-0 h-20 bg-background/80 backdrop-blur-lg border-b border-border/50 z-40" />
      
      <nav className="fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/30 rounded-full blur-md group-hover:blur-lg transition-all duration-300 animate-pulse" />
                <img 
                  src={logo} 
                  alt="HackAI Logo" 
                  className="relative h-10 w-10 object-contain animate-glow" 
                />
              </div>
              <div className="space-y-0">
                <div className="text-xl font-bold font-mono text-primary neon-glow" data-text="HACK_AI">
                  HACK_AI
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  ~/austin/ai/meetup
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item, index) => (
                <NavLink 
                  key={index}
                  path={item.path} 
                  label={item.label} 
                  icon={item.icon}
                  action={item.action}
                />
              ))}
              {user && (
                <NavLink 
                  path="/projects" 
                  label="./projects" 
                  icon={Upload}
                />
              )}
            </div>

            {/* User section */}
            <div className="hidden lg:flex items-center space-x-3">
              {user && (
                <>
                  <div className="flex items-center space-x-2 px-3 py-1 rounded-md bg-card/50 border border-border/50">
                    <User className="h-4 w-4 text-primary" />
                    <span className="text-sm font-mono text-muted-foreground">
                      {user.email?.split('@')[0] || 'user'}
                    </span>
                  </div>
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    size="sm"
                    className="font-mono border-destructive/50 text-destructive hover:bg-destructive/10"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    ./logout
                  </Button>
                </>
              )}
            </div>

            {/* Mobile menu trigger */}
            <div className="lg:hidden">
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-primary/50"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent 
                  side="right" 
                  className="w-80 bg-background/95 backdrop-blur-xl border-l border-border/50"
                >
                  <div className="space-y-6 mt-8">
                    {/* Mobile logo */}
                    <div className="flex items-center space-x-3 pb-4 border-b border-border/50">
                      <Terminal className="h-6 w-6 text-primary" />
                      <div>
                        <div className="font-bold font-mono text-primary">HACK_AI</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          Mobile Terminal
                        </div>
                      </div>
                    </div>

                    {/* Mobile navigation */}
                    <div className="space-y-2">
                      {navItems.map((item, index) => (
                        <NavLink 
                          key={index}
                          path={item.path} 
                          label={item.label} 
                          icon={item.icon}
                          action={item.action}
                          onClick={() => setIsOpen(false)}
                        />
                      ))}
                      {user && (
                        <NavLink 
                          path="/projects" 
                          label="./projects" 
                          icon={Upload}
                          onClick={() => setIsOpen(false)}
                        />
                      )}
                      
                      {/* Contact button in mobile */}
                      <button
                        onClick={handleContactClick}
                        className="flex items-center space-x-2 px-3 py-2 rounded-md font-mono text-sm transition-all duration-300 group w-full text-left text-muted-foreground hover:text-primary hover:bg-primary/10 border border-transparent"
                      >
                        <Mail className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        <span>./contact</span>
                      </button>
                    </div>

                    {/* Mobile user section */}
                    {user && (
                      <div className="pt-4 border-t border-border/50 space-y-3">
                        <div className="flex items-center space-x-2 px-3 py-2 rounded-md bg-card/50 border border-border/50">
                          <User className="h-4 w-4 text-primary" />
                          <span className="text-sm font-mono text-muted-foreground">
                            {user.email?.split('@')[0] || 'user'}
                          </span>
                        </div>
                        <Button
                          onClick={() => {
                            handleLogout();
                            setIsOpen(false);
                          }}
                          variant="outline"
                          className="w-full font-mono border-destructive/50 text-destructive hover:bg-destructive/10"
                        >
                          <LogOut className="h-4 w-4 mr-2" />
                          ./logout
                        </Button>
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;